import {
  Notification,
  UsersNotificationType,
} from "@/models/notification/Notifications";
import { WorkspaceRole } from "@/models/workspace/Workspace";

// Convert notification type to enum safely
export const convertNotificationType = (
  type: string | number
): UsersNotificationType => {
  if (typeof type === "number") {
    return Object.values(UsersNotificationType)[type] as UsersNotificationType;
  }
  return type as UsersNotificationType;
};

export const getNotificationText = (notification: Notification) => {
  switch (notification.type) {
    case UsersNotificationType.WorkspaceFolderAdded:
      return `'${notification.itemsChanged[0]}' er blevet oprettet i '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceDocumentUpdated:
      return `'${notification.itemsChanged[0]}' er blevet opdateret i '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceDocumentUploaded:
      return `'${notification.itemsChanged[0]}' er blevet uploadet til '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceDocumentsUploaded:
      return `${notification.itemsChanged[0]} dokumenter er blevet uploadet til '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceDocumentRemoved:
      return `'${notification.itemsChanged[0]}' er blevet fjernet fra '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceAccessGranted:
      return `Du har fået adgang til '${notification.itemsChanged[1]}' med ${
        (notification.itemsChanged[0] as WorkspaceRole) === WorkspaceRole.OWNER
          ? "ejer"
          : notification.itemsChanged[0] === WorkspaceRole.READ
            ? "læse"
            : notification.itemsChanged[0] === WorkspaceRole.WRITE
              ? "upload "
              : "administrator"
      }adgang.`;
    case UsersNotificationType.WorkspaceAccessRevoked:
      return `Du har mistet adgang til '${notification.itemsChanged[0]}'`;
    case UsersNotificationType.DocumentUpdated:
      return `Dokument ${notification.entityPath} er blevet opdateret.`;
    case UsersNotificationType.CommentAdded:
      return `Kommentar til dokument ${notification.entityPath} er blevet tilføjet.`;
    case UsersNotificationType.ConversationStarted:
      return `Samtale ${notification.entityPath} er blevet startet.`;
    case UsersNotificationType.MessageReceived:
      return `Besked ${notification.entityPath} er blevet modtaget.`;
    case UsersNotificationType.PiiOverride:
      return "En medarbejder har tilsidesat anonymisering af persondata i en chat.";
    case UsersNotificationType.WorkspaceDeleted:
      return `Et workspace du havde adgang til: '${notification.itemsChanged[0]}' er blevet slettet`;
    case UsersNotificationType.WorkspaceDocumentsMoved:
      return `${
        parseInt(notification.itemsChanged[0]) > 1
          ? `${notification.itemsChanged[0]} dokumenter`
          : "Et dokument"
      }  er blevet rykket ind i mappen: '${
        notification.itemsChanged[1]
      }' i workspace: '${notification.itemsChanged[2]}'`;
    case UsersNotificationType.WorkspaceFolderMoved:
      return `Mappen: '${notification.itemsChanged[0]}' er blevet rykket ind i: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceFolderMovedToRoot:
      return `Mappen: '${notification.itemsChanged[0]}' er blevet rykket til roden i workspace: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceFolderRemoved:
      return `Mappen: '${notification.itemsChanged[0]}' er blevet slettet i workspace: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceUpdated:
      return `Workspace: '${notification.itemsChanged[0]}' er blevet opdateret`;
    case UsersNotificationType.WorkspaceFolderUpdated:
      return `Mappe omdøbt til: '${notification.itemsChanged[0]}' i workspace: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceLinkAdded:
      return `Linket: '${notification.itemsChanged[0]}' er blevet tilføjet i workspace: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceLinkUpdated:
      return `Linket: '${notification.itemsChanged[0]}' er blevet opdateret i workspace: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceLinkMoved:
      return `Linket: '${notification.itemsChanged[0]}' er blevet rykket ind i: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceLinkMovedToRoot:
      return `Linket: '${notification.itemsChanged[0]}' er blevet rykket til roden i workspace: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceLinkRemoved:
      return `Linket: '${notification.itemsChanged[0]}' er blevet slettet i workspace: '${notification.itemsChanged[1]}'`;
    case UsersNotificationType.WorkspaceCommentCreated:
      return `Det er en ny kommentar på workspace: '${notification.itemsChanged[0]}'`;
    case UsersNotificationType.WorkspaceCommentReacted:
      return "Din kommentar har fået ny reaktion";
    case UsersNotificationType.WorkspaceCommentRepliedTo:
      return "Din kommentar har fået et nyt svar";
    case UsersNotificationType.WorkspaceCommentUpdated:
      return "En kommentar du har besvaret er blevet opdateret";
    case UsersNotificationType.WorkspaceRoleUpdated:
      return `Adgangsniveau for workspace '${
        notification.itemsChanged[1]
      }' er blevet opdateret til ${
        (notification.itemsChanged[0] as WorkspaceRole) === WorkspaceRole.OWNER
          ? "ejer"
          : notification.itemsChanged[0] === WorkspaceRole.READ
            ? "læse"
            : notification.itemsChanged[0] === WorkspaceRole.WRITE
              ? "upload "
              : "administrator"
      }adgang.`;
    default:
      return notification.text;
  }
};

export const getNotificationCategory = (notification: Notification) => {
  switch (notification.type) {
    case UsersNotificationType.WorkspaceFolderMoved:
    case UsersNotificationType.WorkspaceFolderMovedToRoot:
    case UsersNotificationType.WorkspaceFolderAdded:
    case UsersNotificationType.WorkspaceFolderRemoved:
    case UsersNotificationType.WorkspaceFolderUpdated:
      return "Workspace mapper";
    case UsersNotificationType.WorkspaceDocumentUpdated:
    case UsersNotificationType.WorkspaceDocumentUploaded:
    case UsersNotificationType.WorkspaceDocumentsUploaded:
    case UsersNotificationType.WorkspaceDocumentRemoved:
    case UsersNotificationType.WorkspaceDocumentsMoved:
    case UsersNotificationType.WorkspaceDocumentsMovedToRoot:
      return "Workspace dokumenter";
    case UsersNotificationType.WorkspaceLinkAdded:
    case UsersNotificationType.WorkspaceLinkUpdated:
    case UsersNotificationType.WorkspaceLinkMoved:
    case UsersNotificationType.WorkspaceLinkMovedToRoot:
    case UsersNotificationType.WorkspaceLinkRemoved:
      return "Workspace links";
    case UsersNotificationType.DocumentLinkAutomaticallyUpdated:
    case UsersNotificationType.LegislationUpdated:
    case UsersNotificationType.LegislationCreated:
    case UsersNotificationType.LegislationIsHistoric:
    case UsersNotificationType.LegislationMetadataUpdated:
    case UsersNotificationType.LegislationTimelineChanged:
    case UsersNotificationType.LegislationSubscriptionUpdated:
      return "Kilde";
    case UsersNotificationType.WorkspaceAccessGranted:
    case UsersNotificationType.WorkspaceAccessRevoked:
    case UsersNotificationType.WorkspaceDeleted:
    case UsersNotificationType.WorkspaceUpdated:
    case UsersNotificationType.WorkspaceRoleUpdated:
      return "Workspace";
    case UsersNotificationType.WorkspaceCommentCreated:
    case UsersNotificationType.WorkspaceCommentReacted:
    case UsersNotificationType.WorkspaceCommentRepliedTo:
    case UsersNotificationType.WorkspaceCommentUpdated:
      return "Workspace kommentarer";
    case UsersNotificationType.GroupMessage:
    case UsersNotificationType.GroupMemberAdded:
    case UsersNotificationType.GroupDeleted:
    case UsersNotificationType.GroupMemberRemoved:
    case UsersNotificationType.GroupNameUpdated:
    case UsersNotificationType.GroupAddedToSubscription:
    case UsersNotificationType.GroupRemovedFromSubscription:
      return "Gruppe";
    case UsersNotificationType.PiiOverride:
      // Deliberate: PII overrides happen in chat, but there is no chat-ish
      // category (ConversationStarted/MessageReceived also land in "System"),
      // and the audience is admins — so keep the "System" bucket explicitly.
      return "System";
    default:
      return "System";
  }
};

export const formatNotificationType = (type: string): string => {
  switch (type) {
    case "LegislationUpdated":
      return "Kilde opdateret";
    case "LegislationCreated":
      return "Ny kilde";
    case "LegislationIsHistoric":
      return "Kilde gjort historisk";
    case "LegislationMetadataUpdated":
      return "Kilde's metadata opdateret";
    case "LegislationTimelineChanged":
      return "Kilde's tidslinje opdateret";
    case "LegislationSubscriptionUpdated":
      return "Kilde abonnement opdateret";
    case "WorkspaceDocumentUpdated":
      return "Dokument opdateret";
    case "DocumentLinkAutomaticallyUpdated":
      return "Dokumentlink automatisk opdateret";
    case "WorkspaceDocumentUploaded":
      return "Dokument uploadet";
    case "WorkspaceDocumentsUploaded":
      return "Dokumenter uploadet";
    case "WorkspaceDocumentRemoved":
      return "Dokument fjernet";
    case "WorkspaceAccessGranted":
      return "Adgang givet";
    case "WorkspaceAccessRevoked":
      return "Adgang fjernet";
    case "DocumentUpdated":
      return "Dokument ændret";
    case "CommentAdded":
      return "Kommentar tilføjet";
    case "ConversationStarted":
      return "Samtale startet";
    case "MessageReceived":
      return "Besked modtaget";
    case "PiiOverride":
      return "PII-tilsidesættelse";
    case "ReviewCompleted":
      return "Gennemgang afsluttet";
    case "SystemAlert":
      return "System advarsel";
    case "UserMentioned":
      return "Bruger nævnt";
    case "WorkspaceFolderAdded":
      return "Ny mappe";
    case "WorkspaceDeleted":
      return "Slettet workspace";
    case "WorkspaceDocumentsMoved":
    case "WorkspaceDocumentsMovedToRoot":
      return "Dokumenter rykket";
    case "WorkspaceFolderMovedToRoot":
    case "WorkspaceFolderMoved":
      return "Mappe rykket";
    case "WorkspaceFolderRemoved":
      return "Mappe slettet";
    case "WorkspaceUpdated":
      return "Workspace opdateret";
    case "WorkspaceFolderUpdated":
      return "Mappe omdøbt";
    case "WorkspaceLinkAdded":
      return "Nyt link";
    case "WorkspaceLinkUpdated":
      return "Link opdateret";
    case "WorkspaceLinkMoved":
    case "WorkspaceLinkMovedToRoot":
      return "Link rykket";
    case "WorkspaceLinkRemoved":
      return "Link slettet";
    case "WorkspaceCommentCreated":
      return "Ny kommentar";
    case "WorkspaceCommentReacted":
      return "Ny reaktion";
    case "WorkspaceCommentRepliedTo":
      return "Nyt reply";
    case "WorkspaceCommentUpdated":
      return "Kommentar opdatering";
    case "WorkspaceRoleUpdated":
      return "Adgangsniveau opdateret";
    case "GroupMessage":
      return "Gruppe besked";
    case "GroupMemberAdded":
      return "Tilføjet til gruppe";
    case "GroupMemberRemoved":
      return "Fjernet fra gruppe";
    case "GroupNameUpdated":
      return "Gruppe opdateret";
    case "GroupDeleted":
      return "Gruppe fjernet";
    case "GroupAddedToSubscription":
      return "Abonnement tilføjet til gruppe";
    case "GroupRemovedFromSubscription":
      return "Abonnement fjernet fra gruppe";
    default:
      return type;
  }
};

/** Actor names that are shown as "System-genereret" in the dashboard. */
const SYSTEM_ACTOR_DISPLAY_NAME = "System-genereret";
const ACTOR_NAMES_MAPPED_TO_SYSTEM: string[] = [
  "System",
  "Post-processing Python worker",
  "Console ServiceBus sender app",
];

/** Display name for the actor (Aktør). Maps System and known system/worker names to Danish "System-genereret". */
export const getNotificationActorDisplayName = (
  notification: Notification
): string => {
  const name = notification.actionCausedByName?.trim() ?? "";
  const isMapped =
    name.toLowerCase() === "system" ||
    ACTOR_NAMES_MAPPED_TO_SYSTEM.some(
      (mapped) => mapped.trim().toLowerCase() === name.toLowerCase()
    );
  return isMapped ? SYSTEM_ACTOR_DISPLAY_NAME : name;
};